/**
 * Notification frequency — the single per-user "Timesheet email frequency"
 * choice, and the value stored on each preference row. Shared by the API (the
 * send path maps it to a batch release cron) and the web client (renders the
 * options and current selection).
 *
 * `immediate` means genuinely immediate — per-event, no batching — the same
 * intent for reminders and digest emails. The other three coalesce events
 * into one digest per window. The frequency → cron mapping lives in
 * `@clockwise/notifications` (`preferences/frequency.ts`); this module is just
 * the enum + labels so the web layer can depend on it without pulling in the
 * server package.
 */

export const NOTIFICATION_FREQUENCIES = ['immediate', 'fiveMinutes', 'hourly', 'daily'] as const;

export type NotificationFrequency = (typeof NOTIFICATION_FREQUENCIES)[number];

/** The default when a user has never chosen: one digest per day. */
export const DEFAULT_NOTIFICATION_FREQUENCY: NotificationFrequency = 'daily';

/** Human labels for the frequency control, keyed by frequency. */
export const NOTIFICATION_FREQUENCY_LABELS: Record<NotificationFrequency, string> = {
  immediate: 'Immediate',
  fiveMinutes: 'Every 5 minutes',
  hourly: 'Every hour',
  daily: 'Once a day',
};

/** True iff `value` is one of the known frequencies. */
export function isNotificationFrequency(value: unknown): value is NotificationFrequency {
  return (
    typeof value === 'string' && (NOTIFICATION_FREQUENCIES as readonly string[]).includes(value)
  );
}
